import { FC, ReactNode, useEffect } from "react";
import { generatePath, Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store/store";
import { STAFF_USER_ROUTE } from "../../../app/router/routes";
import { getTrialLessonTagName, ITrialLesson, TTrialLessonAction, TTrialLessonLostType, TTrialLessonStatus } from "../../../entities/trialLesson";
import { MyButton } from "../../../shared/ui/button";
import { ArrowLeftIcon, BanIcon, CalendarIcon, CheckIcon, ClockIcon, EditIcon, InfoIcon, 
    RescheduleIcon, StudentIcon, TelegramIcon, TrashIcon, TutorIcon } from "../../../shared/ui/icon";
import classes from './trialLessonCard.module.scss'
import { formatMoscowDate, formatMoscowTime, isSameMoscowDate } from "../../../shared/lib/helpers/moscowDateTime";

interface IProps {
    trialLesson: ITrialLesson;
    startsAt: Date;
    endsAt: Date;
    formatTime: (date: Date) => string;
    onBack: () => void;
    onAction?: (action: TTrialLessonAction) => void;
    onMount?: () => void;
}

const STATUS_LABELS: Record<TTrialLessonStatus, string> = {
    TRIAL_LESSON_STATUS_UNSPECIFIED: 'Неизвестно',
    TRIAL_LESSON_STATUS_ASSIGNED: 'Назначено',
    TRIAL_LESSON_STATUS_COMPLETED: 'Проведено',
    TRIAL_LESSON_STATUS_STUDENT_CREATED: 'Студент создан',
    TRIAL_LESSON_STATUS_LOST: 'Слив',
    TRIAL_LESSON_STATUS_ORANGE_PENDING: 'Проведено (Требует обработки)',
}

const ACTION_LABELS: Partial<Record<TTrialLessonAction, string>> = {
    TRIAL_LESSON_ACTION_COMPLETE: 'Провести',
    TRIAL_LESSON_ACTION_CREATE_STUDENT: 'Создать студента',
    TRIAL_LESSON_ACTION_MARK_LOST: 'Слив (отменить)',
    TRIAL_LESSON_ACTION_REOPEN_LOST: 'Откатить слив',
    TRIAL_LESSON_ACTION_EDIT: 'Редактировать',
    TRIAL_LESSON_ACTION_DELETE: 'Удалить',
    TRIAL_LESSON_ACTION_RESCHEDULE: 'Перенести',
}

const ACTION_ICONS: Partial<Record<TTrialLessonAction, ReactNode>> = {
    TRIAL_LESSON_ACTION_COMPLETE: <CheckIcon />,
    TRIAL_LESSON_ACTION_CREATE_STUDENT: <StudentIcon />,
    TRIAL_LESSON_ACTION_MARK_LOST: <BanIcon />,
    TRIAL_LESSON_ACTION_REOPEN_LOST: <CheckIcon />,
    TRIAL_LESSON_ACTION_EDIT: <EditIcon />,
    TRIAL_LESSON_ACTION_DELETE: <TrashIcon />,
    TRIAL_LESSON_ACTION_RESCHEDULE: <RescheduleIcon />,
}

const LOST_TYPE_LABELS: Partial<Record<TTrialLessonLostType, string>> = {
    TRIAL_LESSON_LOST_TYPE_BEFORE_TRIAL: 'До пробного',
    TRIAL_LESSON_LOST_TYPE_AFTER_TRIAL: 'После пробного',
}

const getStatusClass = (status: TTrialLessonStatus): string => {
    if(status === 'TRIAL_LESSON_STATUS_COMPLETED') return classes.completed
    if(status === 'TRIAL_LESSON_STATUS_STUDENT_CREATED') return classes.studentCreated
    if(status === 'TRIAL_LESSON_STATUS_LOST') return classes.lost
    if(status === 'TRIAL_LESSON_STATUS_ORANGE_PENDING') return classes.orange

    return classes.assigned
}

const getDateLabel = (date: Date): string => {
    return formatMoscowDate(date, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

const getShortDateLabel = (date: Date): string => {
    return formatMoscowDate(date, {
        day: '2-digit',
        month: '2-digit',
    })
}

const getDateTimeRangeLabel = (
    startsAt: Date,
    endsAt: Date,
    formatTime: (date: Date) => string
): string => {
    if(isSameMoscowDate(startsAt, endsAt)){
        return `${formatTime(startsAt)}–${formatTime(endsAt)}`
    }

    return `${getShortDateLabel(startsAt)} ${formatTime(startsAt)}–${getShortDateLabel(endsAt)} ${formatTime(endsAt)}`
}

const getDateTimeLabel = (value: string): string => {
    const date = new Date(value)
    return `${getDateLabel(date)} ${formatMoscowTime(value)}`
}

export const TrialLessonCard: FC<IProps> = ({trialLesson, startsAt, endsAt, formatTime, onBack, onAction, onMount}) => {
    const {my} = useAppSelector(state => state.myReducer)
    const canViewLostDetails = my.role !== 'tutor'
    const lessonTagName = getTrialLessonTagName(trialLesson)
    const canViewPrivateInfo = trialLesson.availableActions.includes('TRIAL_LESSON_ACTION_VIEW_PRIVATE_INFO')
    const actions = trialLesson.availableActions.filter(action => (
        action !== 'TRIAL_LESSON_ACTION_UNSPECIFIED' &&
        action !== 'TRIAL_LESSON_ACTION_VIEW_PRIVATE_INFO'
    ))

    useEffect(() => {
        onMount?.()
    }, [onMount])

    return (
        <section className={classes.container}>
            <button type="button" className={classes.backButton} onClick={onBack}>
                <ArrowLeftIcon />
                Вернуться к пробным занятиям
            </button>

            <section className={classes.header}>
                <section className={classes.metaList}>
                    <span className={classes.metaBadge}>
                        <CalendarIcon size={18} />
                        {getDateLabel(startsAt)}
                    </span>
                    <span className={classes.metaBadge}>
                        <ClockIcon size={18} />
                        {getDateTimeRangeLabel(startsAt, endsAt, formatTime)}
                    </span>
                    <span className={`${classes.metaBadge} ${classes.telegramBadge}`}>
                        <TelegramIcon size={18} />
                        {trialLesson.tgAccount.username}
                    </span>
                </section>

                <section className={classes.statusWrap}>
                    <span className={classes.statusLabel}>Статус:</span>
                    <span className={`${classes.statusBadge} ${getStatusClass(trialLesson.status)}`}>
                        {STATUS_LABELS[trialLesson.status]}
                    </span>
                </section>
            </section>

            <section className={classes.sections}>
                <section className={classes.personSection}>
                    <h3 className={classes.sectionTitle}>
                        <TutorIcon />
                        Исполнитель
                    </h3>
                    <section className={classes.personCard}>
                        <span className={classes.personIndex}>1</span>
                        <span className={classes.personName}>{trialLesson.tutor.fullName}</span>
                        {Number(trialLesson.tutor.userId) > 0 && (
                            <Link
                                className={classes.detailsButton}
                                to={generatePath(STAFF_USER_ROUTE.path, {user_id: trialLesson.tutor.userId})}
                            >
                                Подробнее
                            </Link>
                        )}
                    </section>
                </section>

                <section className={classes.personSection}>
                    <h3 className={classes.sectionTitle}>
                        <StudentIcon />
                        Ученик из заявки
                    </h3>
                    <section className={classes.personCard}>
                        <span className={classes.personIndex}>1</span>
                        <section className={classes.personContent}>
                            <span className={classes.personName}>{trialLesson.studentName}</span>
                            {trialLesson.studentTg && <span className={classes.personSub}>@{trialLesson.studentTg}</span>}
                        </section>
                    </section>
                </section>
            </section>

            <section className={classes.infoGrid}>
                <section className={classes.infoItem}>
                    <span className={classes.infoLabel}>Тег</span>
                    <span className={classes.infoValue}>{lessonTagName}</span>
                </section>
                {trialLesson.publicComment && (
                    <section className={classes.infoItem}>
                        <span className={classes.infoLabel}>Комментарий</span>
                        <span className={classes.infoValue}>{trialLesson.publicComment}</span>
                    </section>
                )}
                {trialLesson.completedAt && (
                    <section className={classes.infoItem}>
                        <span className={classes.infoLabel}>Отметка проведения</span>
                        <span className={classes.infoValue}>{getDateTimeLabel(trialLesson.completedAt)}</span>
                    </section>
                )}
                {canViewPrivateInfo && trialLesson.privateInfo && (
                    <section className={`${classes.infoItem} ${classes.wideInfo}`}>
                        <span className={classes.infoLabel}>
                            <InfoIcon size={18} />
                            Приватная информация
                        </span>
                        <span className={classes.infoValue}>{trialLesson.privateInfo}</span>
                    </section>
                )}
                {canViewLostDetails && trialLesson.lostComment && (
                    <section className={`${classes.infoItem} ${classes.wideInfo}`}>
                        <span className={classes.infoLabel}>Причина слива</span>
                        <span className={classes.infoValue}>{trialLesson.lostComment}</span>
                    </section>
                )}
                {canViewLostDetails && trialLesson.lostType && trialLesson.lostType !== 'TRIAL_LESSON_LOST_TYPE_UNSPECIFIED' && (
                    <section className={classes.infoItem}>
                        <span className={classes.infoLabel}>Тип слива</span>
                        <span className={classes.infoValue}>{LOST_TYPE_LABELS[trialLesson.lostType] ?? trialLesson.lostType}</span>
                    </section>
                )}
            </section>

            {Boolean(actions.length) && (
                <section className={classes.actionsBlock}>
                    <h3 className={classes.actionsTitle}>Действия с пробным</h3>
                    <section className={classes.actionsList}>
                        {actions.map(action => (
                            <section
                                key={action}
                                className={`${classes.actionButton} ${action === 'TRIAL_LESSON_ACTION_DELETE' || action === 'TRIAL_LESSON_ACTION_MARK_LOST' ? classes.dangerAction : ''}`}
                            >
                                <MyButton onClick={() => onAction?.(action)}>
                                    <span className={classes.actionButtonContent}>
                                        {ACTION_ICONS[action]}
                                        {ACTION_LABELS[action] ?? action}
                                    </span>
                                </MyButton>
                            </section>
                        ))}
                    </section>
                </section>
            )}
        </section>
    )
}
